import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from 'context/AuthContext';
import {
  getGroupedPermissions,
  getUserPreferences,
  getUserProfileData
} from 'helpers/userProfile';
import React, { useState } from 'react';
import { Badge, Card, Col, ListGroup, Nav, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Banner from './Banner';

const MODULE_ICONS = {
  integrations: 'plug',
  posts: 'tags',
  stock: 'box',
  questions: 'question-circle',
  sales: 'shopping-cart',
  claims: 'exclamation-triangle',
  calendar: 'calendar-alt',
  partners: 'users',
  blog: 'pen-nib',
  messages: 'comments',
  agents: 'chess-rook',
  notifications: 'bell',
  dev_testing: 'tools',
  profiles: 'user-circle',
  users: 'users',
  bans: 'ban',
  general: 'shield-alt'
};

const ACTION_ICONS = {
  view: 'eye',
  create: 'plus',
  update: 'pencil-alt',
  delete: 'trash',
  connect: 'link',
  disconnect: 'unlink',
  sync: 'sync-alt',
  run: 'play',
  view_internal: 'building',
  send_internal: 'paper-plane',
  view_mercadolibre: 'store',
  view_questions: 'question-circle'
};

const Profile = () => {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const {
    membership, company, profile, permissions,
    user: currentUser, email, username,
    firstName, lastName, companyName, profileName,
    membershipStatus, joinedAt
  } = getUserProfileData(userData);
  const preferences = getUserPreferences(userData?.userId);
  const groupedPermissions = getGroupedPermissions(permissions);

  const membershipEntries = Object.entries(membership || {}).filter(
    ([, value]) =>
      value !== null &&
      value !== undefined &&
      value !== '' &&
      typeof value !== 'object'
  );

  const companyEntries = Object.entries(company || {}).filter(
    ([, value]) =>
      value !== null &&
      value !== undefined &&
      value !== '' &&
      typeof value !== 'object'
  );

  const profileEntries = Object.entries(profile || {}).filter(
    ([, value]) =>
      value !== null &&
      value !== undefined &&
      value !== '' &&
      typeof value !== 'object'
  );

  const statusLabel =
    typeof membershipStatus === 'boolean'
      ? membershipStatus
        ? t('USER_PROFILE.STATUS.ACTIVE')
        : t('USER_PROFILE.STATUS.INACTIVE')
      : membershipStatus || t('USER_PROFILE.NOT_AVAILABLE');

  const overviewDetails = [
    { icon: 'user', label: t('USER_PROFILE.FIELDS.FIRST_NAME'), value: firstName || t('USER_PROFILE.NOT_AVAILABLE') },
    { icon: 'user', label: t('USER_PROFILE.FIELDS.LAST_NAME'), value: lastName || t('USER_PROFILE.NOT_AVAILABLE') },
    { icon: 'id-badge', label: t('USER_PROFILE.FIELDS.USERNAME'), value: username || t('USER_PROFILE.NOT_AVAILABLE') },
    { icon: 'envelope', label: t('USER_PROFILE.FIELDS.EMAIL'), value: email || t('USER_PROFILE.NOT_AVAILABLE') },
    { icon: 'phone', label: t('USER_PROFILE.FIELDS.PHONE'), value: preferences.phone || t('USER_PROFILE.NOT_AVAILABLE') },
    { icon: 'building', label: t('USER_PROFILE.FIELDS.COMPANY'), value: companyName || t('USER_PROFILE.NOT_ASSIGNED') },
    { icon: 'user-circle', label: t('USER_PROFILE.FIELDS.PROFILE'), value: profileName || t('USER_PROFILE.NOT_ASSIGNED') },
    { icon: 'toggle-on', label: t('USER_PROFILE.FIELDS.MEMBERSHIP_STATUS'), value: statusLabel },
    { icon: 'calendar-alt', label: t('USER_PROFILE.FIELDS.CREATED_AT'), value: joinedAt || t('USER_PROFILE.NOT_AVAILABLE') }
  ];

  return (
    <>
      <Banner />
      <Card>
        <Card.Header className="p-0 border-bottom">
          <Nav
            variant="tabs"
            activeKey={activeTab}
            onSelect={setActiveTab}
            className="nav-tabs-falcon px-3 pt-2"
          >
            <Nav.Item>
              <Nav.Link eventKey="overview">
                <FontAwesomeIcon icon="user" className="me-2 fs--1" />
                {t('USER_PROFILE.TABS.OVERVIEW')}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="permissions">
                <FontAwesomeIcon icon="shield-alt" className="me-2 fs--1" />
                {t('USER_PROFILE.TABS.PERMISSIONS')}
                <Badge bg="soft-primary" className="text-primary ms-2">
                  {permissions.length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="workspace">
                <FontAwesomeIcon icon="building" className="me-2 fs--1" />
                {t('USER_PROFILE.TABS.WORKSPACE')}
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Card.Header>
        <Card.Body>
          {activeTab === 'overview' && (
            <Row className="g-3">
              <Col lg={6}>
                <ListGroup variant="flush">
                  {overviewDetails.map(item => (
                    <ListGroup.Item
                      key={item.label}
                      className="d-flex justify-content-between align-items-center gap-3 px-0"
                    >
                      <span className="text-600">
                        <FontAwesomeIcon icon={item.icon} fixedWidth className="me-2 fs--1" />
                        {item.label}
                      </span>
                      <span className="text-900 text-end fw-semi-bold">{item.value}</span>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Col>
              <Col lg={6}>
                <Row className="g-3">
                  <Col sm={6}>
                    <div className="border rounded-3 p-3 h-100">
                      <div className="fs--2 text-500 mb-2">
                        <FontAwesomeIcon icon="id-badge" className="me-2" />
                        {t('USER_PROFILE.FIELDS.USER_ID')}
                      </div>
                      <div className="text-900 fw-semi-bold">
                        {currentUser?.id || userData?.userId || t('USER_PROFILE.NOT_AVAILABLE')}
                      </div>
                    </div>
                  </Col>
                  <Col sm={6}>
                    <div className="border rounded-3 p-3 h-100">
                      <div className="fs--2 text-500 mb-2">
                        <FontAwesomeIcon icon="shield-alt" className="me-2" />
                        {t('USER_PROFILE.PERMISSIONS')}
                      </div>
                      <div className="text-900 fw-semi-bold">{permissions.length}</div>
                    </div>
                  </Col>
                  <Col sm={6}>
                    <div className="border rounded-3 p-3 h-100">
                      <div className="fs--2 text-500 mb-2">
                        <FontAwesomeIcon icon="building" className="me-2" />
                        {t('USER_PROFILE.FIELDS.COMPANY')}
                      </div>
                      <div className="text-900 fw-semi-bold">
                        {companyName || t('USER_PROFILE.NOT_ASSIGNED')}
                      </div>
                    </div>
                  </Col>
                  <Col sm={6}>
                    <div className="border rounded-3 p-3 h-100">
                      <div className="fs--2 text-500 mb-2">
                        <FontAwesomeIcon icon="user-circle" className="me-2" />
                        {t('USER_PROFILE.FIELDS.PROFILE')}
                      </div>
                      <div className="text-900 fw-semi-bold">
                        {profileName || t('USER_PROFILE.NOT_ASSIGNED')}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}

          {activeTab === 'permissions' && (
            <>
              <p className="text-600 mb-3">
                {t('USER_PROFILE.PERMISSIONS_VIEW.DESCRIPTION')}
              </p>
              {groupedPermissions.length ? (
                <Row className="g-3">
                  {groupedPermissions.map(group => (
                    <Col md={6} key={group.module}>
                      <div className="border rounded-3 p-3 h-100 bg-light dark__bg-1100">
                        <div className="d-flex align-items-start justify-content-between mb-3">
                          <div className="d-flex align-items-center">
                            <div className="permission-module-icon bg-soft-primary text-primary me-3">
                              <FontAwesomeIcon
                                icon={MODULE_ICONS[group.module] || MODULE_ICONS.general}
                              />
                            </div>
                            <div>
                              <h6 className="mb-1">
                                {t(`ACCESS.PERMISSIONS.MODULES.${group.module}.TITLE`, {
                                  defaultValue: group.module
                                })}
                              </h6>
                              <div className="fs--2 text-600">
                                {t(`ACCESS.PERMISSIONS.MODULES.${group.module}.DESCRIPTION`, {
                                  defaultValue: t('USER_PROFILE.PERMISSIONS_VIEW.DEFAULT_DESCRIPTION')
                                })}
                              </div>
                            </div>
                          </div>
                          <Badge bg="soft-info" className="text-info">
                            {group.items.length}
                          </Badge>
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {group.items.map(item => (
                            <Badge
                              key={item.code}
                              bg="soft-primary"
                              className="text-primary px-3 py-2 d-inline-flex align-items-center gap-2"
                            >
                              <FontAwesomeIcon
                                icon={ACTION_ICONS[item.action] || 'shield-alt'}
                                className="fs--2"
                              />
                              {t(`ACCESS.PERMISSIONS.ACTIONS.${item.action}`, {
                                defaultValue: item.action
                              })}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
              ) : (
                <div className="text-500">{t('USER_PROFILE.PERMISSIONS_VIEW.EMPTY')}</div>
              )}
            </>
          )}

          {activeTab === 'workspace' && (
            <Row className="g-3">
              <Col lg={6}>
                <Card className="border h-100">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">{t('USER_PROFILE.FIELDS.COMPANY')}</h6>
                  </Card.Header>
                  <Card.Body>
                    {companyEntries.length ? (
                      companyEntries.map(([key, value]) => (
                        <div key={key} className="d-flex justify-content-between gap-3 mb-2">
                          <span className="text-600">{key}</span>
                          <span className="text-900 text-end">{String(value)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-500">{t('USER_PROFILE.COMPANY_EMPTY')}</div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              <Col lg={6}>
                <Card className="border h-100">
                  <Card.Header className="bg-light">
                    <h6 className="mb-0">{t('USER_PROFILE.FIELDS.PROFILE')}</h6>
                  </Card.Header>
                  <Card.Body>
                    {profileEntries.length ? (
                      profileEntries.map(([key, value]) => (
                        <div key={key} className="d-flex justify-content-between gap-3 mb-2">
                          <span className="text-600">{key}</span>
                          <span className="text-900 text-end">{String(value)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="text-500">{t('USER_PROFILE.PROFILE_EMPTY')}</div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
              {membershipEntries.length ? (
                <Col xs={12}>
                  <Card className="border">
                    <Card.Header className="bg-light">
                      <h6 className="mb-0">{t('USER_PROFILE.MEMBERSHIP_DATA')}</h6>
                    </Card.Header>
                    <Card.Body>
                      <Row className="g-3">
                        {membershipEntries.map(([key, value]) => (
                          <Col md={6} key={key}>
                            <div className="border rounded-3 p-3 h-100">
                              <div className="fs--2 text-500 mb-1">{key}</div>
                              <div className="text-900">{String(value)}</div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              ) : null}
            </Row>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default Profile;
